import { sanitizeRichHtml } from "@/lib/sanitize-rich-html";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import slugify from "slugify";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { savePostSchema, categorySchema, SLUG_REGEX } from "./blog-schema";
import { readingMinutes, wordCount, extractPlainText } from "./reading-time";
import { buildToc } from "./toc";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function proxyBlogImg(storagePath: string | null | undefined, w?: number, h?: number, q?: number): string | null {
  if (!storagePath) return null;
  const encoded = storagePath.split("/").map(encodeURIComponent).join("/");
  const params = new URLSearchParams();
  if (w) params.set("w", String(w));
  if (h) params.set("h", String(h));
  if (q) params.set("q", String(q));
  const qs = params.toString();
  return `/api/public/blog-img/${encoded}${qs ? `?${qs}` : ""}`;
}

function makeSlug(s: string) {
  return (
    slugify(s || "", { lower: true, strict: true, trim: true, locale: "bg" })
      .slice(0, 100) || `post-${Date.now().toString(36)}`
  );
}

async function uniqueSlug(sb: any, base: string, ignoreId?: string) {
  let slug = base;
  for (let i = 2; i < 200; i++) {
    const { data } = await sb.from("blog_posts").select("id").eq("slug", slug).limit(1);
    if (!data || data.length === 0 || (ignoreId && data[0].id === ignoreId)) return slug;
    slug = `${base}-${i}`.slice(0, 120);
  }
  return `${base}-${Date.now().toString(36)}`.slice(0, 120);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function assertStaff(ctx: any) {
  const { data } = await ctx.supabase.from("user_roles").select("role").eq("user_id", ctx.userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("staff")) {
    throw new Error("Forbidden");
  }
  return roles as string[];
}

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, storage: undefined as any },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

function shapePublicPost(row: any, category?: any) {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt ?? null,
    content_html: row.content_html ?? "",
    cover_url: proxyBlogImg(row.cover_storage_path, 1600, 900, 82),
    cover_url_card: proxyBlogImg(row.cover_storage_path, 800, 450, 78),
    cover_url_thumb: proxyBlogImg(row.cover_storage_path, 320, 180, 72),
    cover_focal_x: Number(row.cover_focal_x ?? 0.5),
    cover_focal_y: Number(row.cover_focal_y ?? 0.5),
    category: category ?? null,
    tags: row.tags ?? [],
    author_name: row.author_name ?? null,
    published_at: row.published_at,
    reading_minutes: row.reading_minutes ?? 1,
    word_count: row.word_count ?? 0,
    view_count: row.view_count ?? 0,
    featured: !!row.featured,
    meta_title: row.meta_title ?? null,
    meta_description: row.meta_description ?? null,
    og_url: proxyBlogImg(row.og_storage_path ?? row.cover_storage_path, 1200, 630, 82),
  };
}

// ---------------------------------------------------------------------------
// PUBLIC — anon-safe reads
// ---------------------------------------------------------------------------

export const publicListPosts = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        category: z.string().max(80).optional(),
        tag: z.string().max(40).optional(),
        q: z.string().max(80).optional(),
        limit: z.number().int().min(1).max(48).default(12),
        offset: z.number().int().min(0).max(1000).default(0),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const sb = publicClient();
    const limit = data?.limit ?? 12;
    const offset = data?.offset ?? 0;

    let categoryId: string | null = null;
    if (data?.category) {
      const { data: cat } = await sb.from("blog_categories").select("id").eq("slug", data.category).maybeSingle();
      categoryId = cat?.id ?? "__none__";
    }

    let q = sb
      .from("blog_posts")
      .select(
        "id, slug, title, excerpt, cover_storage_path, cover_focal_x, cover_focal_y, category_id, tags, author_name, published_at, reading_minutes, view_count, featured",
        { count: "exact" },
      )
      .order("featured", { ascending: false })
      .order("published_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (categoryId === "__none__") {
      return { posts: [], total: 0, categories: [] };
    }
    if (categoryId) q = q.eq("category_id", categoryId);
    if (data?.tag) q = q.contains("tags", [data.tag]);
    if (data?.q && data.q.replace(/[^\p{L}\p{N}]/gu, "")) {
      const needle = data.q.replace(/[^\p{L}\p{N}\s\-']/gu, " ").trim();
      q = q.or(`title.ilike.%${needle}%,excerpt.ilike.%${needle}%`);
    }

    const [{ data: rows, count }, { data: cats }] = await Promise.all([
      q,
      sb.from("blog_categories").select("id, name, slug, color").order("position"),
    ]);

    const catById = new Map<string, any>();
    for (const c of cats ?? []) catById.set(c.id, c);

    // Attach post count per category
    const { data: countsRaw } = await sb
      .from("blog_posts")
      .select("category_id")
      .not("category_id", "is", null);
    const catCounts = new Map<string, number>();
    for (const r of countsRaw ?? []) {
      catCounts.set(r.category_id, (catCounts.get(r.category_id) ?? 0) + 1);
    }

    const categories = (cats ?? []).map((c: any) => ({ ...c, count: catCounts.get(c.id) ?? 0 }));

    return {
      posts: (rows ?? []).map((r: any) => shapePublicPost(r, catById.get(r.category_id ?? "") ?? null)),
      total: count ?? 0,
      categories,
    };
  });

export const publicGetPost = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1).max(160) }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: row } = await sb
      .from("blog_posts")
      .select("*, blog_categories(id, name, slug, color)")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!row) return null;
    const toc = buildToc(row.content_html || "");
    const post = shapePublicPost({ ...row, content_html: toc.html }, row.blog_categories);

    // Related — same category, exclude self
    let related: any[] = [];
    if (row.category_id) {
      const { data: rel } = await sb
        .from("blog_posts")
        .select(
          "id, slug, title, excerpt, cover_storage_path, cover_focal_x, cover_focal_y, category_id, tags, author_name, published_at, reading_minutes, view_count, featured",
        )
        .neq("id", row.id)
        .eq("category_id", row.category_id)
        .order("published_at", { ascending: false })
        .limit(3);
      related = (rel ?? []).map((r: any) => shapePublicPost(r, row.blog_categories));
    }

    // Prev / Next
    const { data: prev } = await sb
      .from("blog_posts")
      .select("slug, title")
      .lt("published_at", row.published_at)
      .order("published_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const { data: next } = await sb
      .from("blog_posts")
      .select("slug, title")
      .gt("published_at", row.published_at)
      .order("published_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    return { post, toc: toc.items, related, prev: prev ?? null, next: next ?? null };
  });

export const incrementPostView = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.rpc("increment_blog_view", { _post_id: data.id });
    return { ok: true };
  });

export const publicListCategories = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data } = await sb.from("blog_categories").select("id, name, slug, color, description, position").order("position");
  return data ?? [];
});

// ---------------------------------------------------------------------------
// ADMIN — staff only
// ---------------------------------------------------------------------------

export const adminListPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        status: z.enum(["all", "draft", "published", "scheduled", "trash"]).default("all"),
        q: z.string().max(120).optional(),
        category: z.string().uuid().nullable().optional(),
        sort: z.enum(["updated", "published", "views", "title"]).default("updated"),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    let q = sb.from("blog_posts").select("*, blog_categories(id, name, slug, color)").limit(300);

    if (data?.status === "trash") q = q.not("deleted_at", "is", null);
    else q = q.is("deleted_at", null);

    if (data?.status === "draft") q = q.eq("status", "draft");
    if (data?.status === "published") q = q.eq("status", "published");
    if (data?.status === "scheduled") q = q.eq("status", "scheduled");

    if (data?.category) q = q.eq("category_id", data.category);
    if (data?.q) {
      const needle = data.q.replace(/[%_]/g, "");
      q = q.or(`title.ilike.%${needle}%,slug.ilike.%${needle}%`);
    }

    switch (data?.sort ?? "updated") {
      case "published":
        q = q.order("published_at", { ascending: false, nullsFirst: false });
        break;
      case "views":
        q = q.order("view_count", { ascending: false });
        break;
      case "title":
        q = q.order("title", { ascending: true });
        break;
      default:
        q = q.order("updated_at", { ascending: false });
    }

    const { data: rows, error } = await q;
    if (error) throw new Error("Failed to load posts");
    return (rows ?? []).map((r: any) => ({
      id: r.id,
      slug: r.slug,
      title: r.title,
      excerpt: r.excerpt,
      status: r.status,
      featured: r.featured,
      published_at: r.published_at,
      updated_at: r.updated_at,
      created_at: r.created_at,
      view_count: r.view_count,
      word_count: r.word_count,
      reading_minutes: r.reading_minutes,
      author_name: r.author_name,
      tags: r.tags ?? [],
      category: r.blog_categories ?? null,
      deleted_at: r.deleted_at,
      thumbnail_url: proxyBlogImg(r.cover_storage_path, 160, 90, 72),
    }));
  });

export const adminGetPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data: p, error } = await sb
      .from("blog_posts")
      .select("*, blog_categories(id, name, slug, color)")
      .eq("id", data.id)
      .maybeSingle();
    if (error || !p) throw new Error("Not found");
    return {
      ...p,
      cover_url: proxyBlogImg(p.cover_storage_path, 1200, 675, 78),
      og_url: proxyBlogImg(p.og_storage_path ?? p.cover_storage_path, 1200, 630, 78),
    };
  });

export const adminCreateDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const roles = await assertStaff(context as any);
    const sb = (context as any).supabase;
    const userId = (context as any).userId as string;
    const { data: prof } = await sb.from("profiles").select("first_name, last_name").eq("user_id", userId).maybeSingle();
    const authorName =
      [prof?.first_name, prof?.last_name].filter(Boolean).join(" ") ||
      (roles.includes("admin") ? "FIXHEN Team" : "FIXHEN Staff");
    const baseSlug = `post-${Date.now().toString(36)}`;
    let slug = await uniqueSlug(sb, baseSlug);
    let inserted: any = null;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const { data, error } = await sb
        .from("blog_posts")
        .insert({
          title: "Нов пост",
          slug,
          content_html: "",
          status: "draft",
          author_id: userId,
          author_name: authorName,
        })
        .select("id")
        .single();
      if (!error) { inserted = data; break; }
      if ((error as any).code === "23505") {
        slug = `${baseSlug}-${attempt + 2}`.slice(0, 120);
        continue;
      }
      throw new Error(error.message);
    }
    if (!inserted) throw new Error("Failed to allocate a unique slug.");
    return { id: inserted.id as string };
  });

export const adminSavePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => savePostSchema.parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    if (!data.id) throw new Error("Missing id");

    const words = wordCount(data.content_html);
    const minutes = readingMinutes(data.content_html);

    let slug = data.slug?.trim().toLowerCase();
    if (!slug || !SLUG_REGEX.test(slug)) slug = makeSlug(data.title);
    slug = await uniqueSlug(sb, slug, data.id);

    let publishedAt: string | null = data.published_at ?? null;
    if (data.status === "published" && !publishedAt) publishedAt = new Date().toISOString();
    if (data.status === "scheduled" && !publishedAt) throw new Error("Планираните постове изискват дата");
    if (data.status === "scheduled" && publishedAt && new Date(publishedAt).getTime() <= Date.now()) {
      // Auto-promote if scheduled date is in past
      // Fall through as "published"
      // eslint-disable-next-line no-param-reassign
      data.status = "published";
    }

    const patch = {
      title: data.title.trim(),
      slug,
      excerpt: data.excerpt?.trim() || null,
      content_html: sanitizeRichHtml(data.content_html),
      cover_storage_path: data.cover_storage_path || null,
      cover_focal_x: data.cover_focal_x,
      cover_focal_y: data.cover_focal_y,
      category_id: data.category_id || null,
      tags: data.tags ?? [],
      status: data.status,
      published_at: data.status === "draft" ? null : publishedAt,
      featured: data.featured,
      reading_minutes: minutes,
      word_count: words,
      meta_title: data.meta_title?.trim() || null,
      meta_description: data.meta_description?.trim() || null,
      og_storage_path: data.og_storage_path || null,
    };

    const { error } = await sb.from("blog_posts").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, slug };
  });

export const adminDuplicatePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data: src, error } = await sb.from("blog_posts").select("*").eq("id", data.id).single();
    if (error || !src) throw new Error("Not found");
    const baseSlug = await uniqueSlug(sb, `${src.slug}-kopie`);
    const insertPayload: any = { ...src };
    delete insertPayload.id;
    delete insertPayload.created_at;
    delete insertPayload.updated_at;
    insertPayload.slug = baseSlug;
    insertPayload.title = `${src.title} (копие)`;
    insertPayload.status = "draft";
    insertPayload.published_at = null;
    insertPayload.view_count = 0;
    insertPayload.featured = false;
    insertPayload.deleted_at = null;
    const { data: created, error: iErr } = await sb.from("blog_posts").insert(insertPayload).select("id").single();
    if (iErr) throw new Error(iErr.message);
    return { id: created.id as string };
  });

export const adminDeletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid(), hard: z.boolean().default(false) }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    if (data.hard) {
      const { error } = await sb.from("blog_posts").delete().eq("id", data.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await sb.from("blog_posts").update({ deleted_at: new Date().toISOString(), status: "draft" }).eq("id", data.id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const adminRestorePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    await sb.from("blog_posts").update({ deleted_at: null }).eq("id", data.id);
    return { ok: true };
  });

// -------- Categories --------
export const adminListCategories = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data } = await sb.from("blog_categories").select("*").order("position");
    return data ?? [];
  });

export const adminSaveCategory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => categorySchema.parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const slug = data.slug?.trim() || makeSlug(data.name);
    const payload = {
      name: data.name.trim(),
      slug,
      description: data.description?.trim() || null,
      color: data.color,
      position: data.position,
    };
    if (data.id) {
      const { error } = await sb.from("blog_categories").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: created, error } = await sb.from("blog_categories").insert(payload).select("id").single();
    if (error) throw new Error(error.message);
    return { id: created.id as string };
  });

export const adminDeleteCategory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    await sb.from("blog_categories").delete().eq("id", data.id);
    return { ok: true };
  });

// -------- Cover upload signing --------
export const adminSignCoverUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        post_id: z.string().uuid(),
        filename: z.string().min(1).max(200),
        content_type: z.string().max(120),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const isImage = data.content_type.startsWith("image/");
    const isSupportedVideo = data.content_type === "video/mp4";
    if (!isImage && !isSupportedVideo) throw new Error("Поддържат се изображения и MP4 видео.");
    const ext = (data.filename.split(".").pop() ?? "jpg").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 6) || "jpg";
    const path = `covers/${data.post_id}/${Date.now().toString(36)}.${ext}`;
    const { data: signed, error } = await sb.storage.from("blog-images").createSignedUploadUrl(path);
    if (error) throw new Error(error.message);
    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });

// -------- AI Assist --------
export const adminAiAssist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        kind: z.enum([
          "titles",
          "excerpt",
          "meta_description",
          "tags",
          "improve",
          "expand",
          "proofread",
          "continue",
          "outline",
        ]),
        title: z.string().max(200).optional(),
        content: z.string().max(20_000).optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("AI не е конфигуриран");
    const text = data.content ? extractPlainText(data.content).slice(0, 6000) : "";
    const rawHtml = (data.content ?? "").slice(0, 12000);
    const prompts: Record<string, string> = {
      titles: `Suggest 5 catchy English blog post titles, one per line, no numbering. Topic/title: "${data.title ?? ""}". Content: ${text}`,
      excerpt: `Write a short English excerpt (max 200 characters) for a post titled "${data.title ?? ""}" with content: ${text}. Return only the excerpt.`,
      meta_description: `Write an English SEO meta description (140-160 characters) for the post "${data.title ?? ""}" - ${text}. Return only the text.`,
      tags: `Suggest 6-8 relevant tags in English (one per line, no # and no commas) for the post "${data.title ?? ""}": ${text}`,
      improve: `Improve stylistically, edit and clean up the following blog post HTML. The OUTPUT MUST BE IN ENGLISH — if the input is in another language, translate it to natural English. Preserve the structure (h2/h3/p/ul/ol/li/strong/em/a/img/blockquote). Make it more readable, natural, with clear rhythm. Do NOT add new content, do NOT change the meaning. Do not wrap in a code fence — return only clean HTML.\n\nTitle: "${data.title ?? ""}"\n\nHTML:\n${rawHtml}`,
      expand: `Expand the following blog post HTML with more details, examples, and helpful tips. The OUTPUT MUST BE IN ENGLISH — translate any non-English input. Keep the existing structure and add new sections with <h2> and <p>. Tone is expert but accessible — an automotive blog for FIXHEN (Mercedes/BMW parts). Return only clean HTML, no code fence.\n\nTitle: "${data.title ?? ""}"\n\nHTML:\n${rawHtml}`,
      proofread: `Proofread the spelling and grammar of the following HTML. Fix all errors, punctuation and typos. The OUTPUT MUST BE IN ENGLISH — translate any non-English content to natural English. Do NOT change the meaning or structure. Return only the corrected HTML, no code fence.\n\nHTML:\n${rawHtml}`,
      continue: `Continue the following blog post with 1-2 additional sections (<h2> + a few <p>). The OUTPUT MUST BE IN ENGLISH regardless of the input language. Write in the same style and tone. Return ONLY the new HTML to append (without the existing content, no code fence).\n\nTitle: "${data.title ?? ""}"\n\nExisting HTML:\n${rawHtml}`,
      outline: `Create a detailed English HTML outline for a blog post titled "${data.title ?? ""}". Use <h2> for main sections and a few short <p> hints under each. Return only clean HTML, no code fence.`,
    };
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: "You are an expert automotive blog writer for FIXHEN — spare parts for Mercedes and BMW. You always write in clear, natural, professional English, regardless of the language of the input. Never output Bulgarian or any other language — always English." },
          { role: "user", content: prompts[data.kind] },
        ],
      }),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`AI грешка: ${res.status} ${t.slice(0, 120)}`);
    }
    const json = (await res.json()) as any;
    let content: string = json?.choices?.[0]?.message?.content ?? "";
    // Strip accidental code fences
    content = content.replace(/^```(?:html)?\s*/i, "").replace(/```\s*$/i, "").trim();
    if (data.kind === "titles" || data.kind === "tags") {
      const items = content
        .split(/\r?\n/)
        .map((l) => l.replace(/^[-*\d.\s]+/, "").trim())
        .filter((l) => l.length > 1 && l.length < 120);
      return { items };
    }
    return { text: content.trim() };
  });

// -------- Inline image upload signing (for images inside post content) --------
export const adminSignInlineUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        post_id: z.string().uuid(),
        filename: z.string().min(1).max(200),
        content_type: z.string().max(120),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const ext = (data.filename.split(".").pop() ?? "jpg").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 6) || "jpg";
    const path = `inline/${data.post_id}/${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
    const { data: signed, error } = await sb.storage.from("blog-images").createSignedUploadUrl(path);
    if (error) throw new Error(error.message);
    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });

// -------- List existing images already on the site (products + blog) --------
export const adminListSiteImages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        source: z.enum(["all", "products", "blog"]).default("all"),
        q: z.string().max(120).optional(),
        limit: z.number().int().min(1).max(300).default(120),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const source = data?.source ?? "all";
    const limit = data?.limit ?? 120;
    const needle = data?.q?.trim().replace(/[%_]/g, "");

    const items: Array<{
      kind: "product" | "blog-cover" | "blog-inline";
      url: string;
      thumb_url: string;
      alt: string;
      caption: string;
      storage_path: string;
    }> = [];

    if (source === "all" || source === "products") {
      let pq = sb
        .from("product_images")
        .select("storage_path, product_id, products!inner(title, status)")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (needle) pq = pq.ilike("products.title", `%${needle}%`);
      const { data: rows } = await pq;
      for (const r of rows ?? []) {
        const title = (r as any).products?.title ?? "";
        const status = (r as any).products?.status;
        if (status !== "published") continue;
        const encoded = (r as any).storage_path.split("/").map(encodeURIComponent).join("/");
        items.push({
          kind: "product",
          url: `/api/public/img/${encoded}?w=1200&q=82`,
          thumb_url: `/api/public/img/${encoded}?w=240&h=240&q=72`,
          alt: title,
          caption: title,
          storage_path: (r as any).storage_path,
        });
      }
    }

    if (source === "all" || source === "blog") {
      let bq = sb
        .from("blog_posts")
        .select("title, cover_storage_path")
        .not("cover_storage_path", "is", null)
        .order("updated_at", { ascending: false })
        .limit(limit);
      if (needle) bq = bq.ilike("title", `%${needle}%`);
      const { data: rows } = await bq;
      for (const r of rows ?? []) {
        const p = (r as any).cover_storage_path as string;
        if (!p) continue;
        const encoded = p.split("/").map(encodeURIComponent).join("/");
        items.push({
          kind: "blog-cover",
          url: `/api/public/blog-img/${encoded}?w=1200&q=82`,
          thumb_url: `/api/public/blog-img/${encoded}?w=240&h=240&q=72`,
          alt: (r as any).title ?? "",
          caption: (r as any).title ?? "",
          storage_path: p,
        });
      }
    }

    // Deduplicate by storage_path
    const seen = new Set<string>();
    const dedup = items.filter((i) => {
      if (seen.has(i.storage_path)) return false;
      seen.add(i.storage_path);
      return true;
    });

    return dedup.slice(0, limit);
  });